import { useNavigate } from 'react-router-dom';
import { User } from '../../types/user.response';
import Arrow from '../../components/icons/Arrow';
import CourseCardForSelecting from './CourseCard';
import Searchbar from './Searchbar';
import BookOpen from '../../components/icons/BookOpen';
import { CourseOption } from '../../types/course.response';
import { getCourseKey } from '../../utils/courseUtils';
import { getSemesterName } from '../../utils/semesterUtils';

interface CourseManagementSidebarProps {
  user: User | undefined;
  currentCourses: CourseOption[];
  semester: string;
  colorMap: Record<string, string>;
  onAddCourse: (course: CourseOption) => void;
  onDeleteCourse: (course: CourseOption) => void;
  onSectionSelect: (course: CourseOption, type: string, sectionNbr: string) => void;
  onToggleScheduled: (course: CourseOption) => void;
}

export default function CourseManagementSidebar({
  user,
  currentCourses,
  semester,
  colorMap,
  onAddCourse,
  onDeleteCourse,
  onSectionSelect,
  onToggleScheduled,
}: CourseManagementSidebarProps) {
  const navigate = useNavigate();

  const majors = user?.programs.filter(p => p.type === 'major') ?? [];
  const minors = user?.programs.filter(p => p.type === 'minor') ?? [];
  
  return (
    <div className="flex flex-col md:h-full">
      <div className="sticky top-0 z-10 bg-white w-full py-2">

        {/* Searchbar */}
        <div className="sticky top-0 z-10 bg-white w-full py-2">
          <h2 className="text-lg font-semibold text-gray-800 mb-3">{getSemesterName(semester)} Courses</h2>
          <Searchbar onAddCourse={onAddCourse} />
        </div>
      </div>
      
      {/* 课程列表 */}
      <div className="md:flex-1 md:overflow-y-auto py-5">
          {/* scheduled section */}
          {currentCourses.map((courseOption) => {
              const key = getCourseKey(courseOption);
              return (
                <CourseCardForSelecting
                  key={`scheduled-${key}`}
                  courseOption={courseOption}
                  semester={semester}
                  color={colorMap[key] || '#6B7280'}
                  onDeleteCourse={onDeleteCourse}
                  onSectionSelect={onSectionSelect}
                  onToggleScheduled={onToggleScheduled}
                />
              );
          })}
              
          {/* Empty state with prominent buttons */}
          {currentCourses.length === 0 ? (
            <div className="flex flex-col gap-3 mt-6 px-2">
              <div className="mb-3">
                <p className="text-gray-600 text-sm font-medium mb-1.5">No courses added yet</p>
                <p className="text-gray-500 text-xs leading-relaxed">Browse requirements below and add courses to your schedule</p>
              </div>
              {majors.map((major) => (
                <button
                  key={major.programId}
                  onClick={() => navigate(`/programs/${major.programId}`)}
                  className="group relative flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded-lg shadow-md hover:shadow-lg transition-all duration-200 transform hover:scale-[1.02]"
                >
                  <div className="flex-shrink-0">
                    <BookOpen className="w-5 h-5" />
                  </div>
                  <div className="flex-1 text-left">
                    <p className="text-sm font-semibold">{major.name}</p>
                    <p className="text-xs text-blue-100 opacity-90">Browse & consider courses</p>
                  </div>
                  <div className="flex-shrink-0">
                    <Arrow direction="right" className="w-4 h-4 transform group-hover:translate-x-1 transition-transform" color="white" />
                  </div>
                </button>
              ))}
              {minors.map((minor) => (
                <button
                  key={minor.programId}
                  onClick={() => navigate(`/programs/${minor.programId}`)}
                  className="group relative flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white rounded-lg shadow-md hover:shadow-lg transition-all duration-200 transform hover:scale-[1.02]"
                >
                  <div className="flex-shrink-0">
                    <BookOpen className="w-5 h-5" />
                  </div>
                  <div className="flex-1 text-left">
                    <p className="text-sm font-semibold">{minor.name}</p>
                    <p className="text-xs text-purple-100 opacity-90">Browse & consider courses</p>
                  </div>
                  <div className="flex-shrink-0">
                    <Arrow direction="right" className="w-4 h-4 transform group-hover:translate-x-1 transition-transform" color="white" />
                  </div>
                </button>
              ))}
            </div>
          ) : (
            /* Text links when courses exist */
            <div className="mt-8 pt-4 px-2 border-t border-gray-200">
              <p className="text-gray-500 text-xs mb-3">
                Looking for more courses? Browse your programs:
              </p>
              <div className="flex flex-col gap-2">
                {majors.map((major) => (
                  <button
                    key={major.programId}
                    onClick={() => navigate(`/programs/${major.programId}`)}
                    className="text-left text-sm text-blue-600 hover:text-blue-700 underline hover:no-underline transition-all"
                  >
                    {major.name} →
                  </button>
                ))}
                {minors.map((minor) => (
                  <button
                    key={minor.programId}
                    onClick={() => navigate(`/programs/${minor.programId}`)}
                    className="text-left text-sm text-blue-600 hover:text-blue-700 underline hover:no-underline transition-all"
                  >
                    {minor.name} →
                  </button>
                ))}
              </div>
            </div>
          )}
      </div>
    </div>
  );
}
